import React from 'react';
import {Box} from '@mui/material';
import {t} from 'i18next';
import StepNavigation from '../step-navigation/StepNavigation';
import {Step} from '../../../../common/arr-stepper/ArrStepper';
import ArrButton from '../../../../common/arr-button/ArrButton';
import './StepWrapper.scss';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface StepWrapperProps {
  children: React.ReactNode;
  steps: Step[];
  currentStepKey: string;
  isSaveDisabled: boolean;
  isNextDisabled: boolean;
  isBackDisabled: boolean;
  onSave: () => void;
  onNext: () => void;
  onBack?: () => void;
  onStepChange?: (stepKey: string) => void;
  className?: string;
  showInternalHeaderNav?: boolean;
  showHeaderNav?: boolean;
  showFooterNav?: boolean;
  showSaveButton?: boolean;
  showNextButton?: boolean;
}

const StepWrapper: React.FC<StepWrapperProps> = ({
  children,
  steps,
  currentStepKey,
  isSaveDisabled,
  isNextDisabled,
  isBackDisabled,
  onSave,
  onNext,
  onBack,
  className = '',
  showInternalHeaderNav,
  showHeaderNav,
  showFooterNav = true,
  showSaveButton = true,
  showNextButton = true,
}) => {
  // Use showHeaderNav if provided, otherwise fall back to showInternalHeaderNav, default to true
  const shouldShowHeaderNav =
    showHeaderNav !== undefined
      ? showHeaderNav
      : showInternalHeaderNav !== false;
  const currentStepIndex = steps.findIndex(
    (step) => step.key === currentStepKey
  );
  const isFirstStep = currentStepIndex === 0;

  return (
    <Box className={`step-wrapper ${className}`}>
      {/* Navigation below stepper */}
      {shouldShowHeaderNav && (
        <Box className="new-production-section-header">
          <Box className="action-section">
            <Box className="left-action">
              {!isFirstStep && onBack && (
                <ArrButton
                  variant="secondary"
                  size="large"
                  className="back-button"
                  onClick={onBack}
                  disabled={isBackDisabled}
                  dataTestId="step-wrapper-back-button"
                >
                  <ArrowBackIcon />
                  {t('common.back')}
                </ArrButton>
              )}
            </Box>
            <Box className="right-action">
              {showSaveButton && (
                <ArrButton
                  variant="secondary"
                  type="button"
                  size="large"
                  className="save-button"
                  onClick={onSave}
                  disabled={isSaveDisabled}
                  dataTestId="step-wrapper-save-button"
                >
                  {t('common.save')}
                </ArrButton>
              )}
              {showNextButton && (
                <ArrButton
                  variant="primary"
                  type="button"
                  size="large"
                  className="next-button"
                  onClick={onNext}
                  isAriaDisabled={isNextDisabled}
                  dataTestId="step-wrapper-next-button"
                >
                  {t('common.next')}
                </ArrButton>
              )}
            </Box>
          </Box>
        </Box>
      )}
      {/* Step Content */}
      <Box className="step-content">{children}</Box>
      {/* Footer Navigation */}
      {showFooterNav && (
        <StepNavigation
          onSave={onSave}
          onNext={onNext}
          isSaveDisabled={isSaveDisabled}
          isNextDisabled={isNextDisabled}
          showBack={false}
          showHeader={false}
          showFooter={true}
          showSaveButton={showSaveButton}
          showNextButton={showNextButton}
        />
      )}
    </Box>
  );
};

export default StepWrapper;
